import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useQuery } from "react-query";
import { Input, Button, Spinner, COLOR_SYSTEM, ToggleButton } from "components/atoms";
import Dropdown, { DropdownMenuItem } from "components/molecules/Dropdown";
import { fetchPatientById } from "api/patientService";
import { CreateGroundTruthDto } from "services/api/aiReviewTest/groundTruthApi";
import { GroundTruth, PromptType, PROMPT_TYPE_KOREAN_NAMES } from "common/models/types";

interface GroundTruthFormProps {
  groundTruth?: GroundTruth;
  onSubmit: (data: CreateGroundTruthDto) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const GroundTruthForm: React.FC<GroundTruthFormProps> = ({
  groundTruth,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const isEditMode = !!groundTruth;

  const [formData, setFormData] = useState({
    patientId: groundTruth?.patientId?.toString() || "",
    promptType: groundTruth?.promptType || "",
    expectedContent: groundTruth?.expectedContent || "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPromptDropdown, setShowPromptDropdown] = useState(false);
  const [showPatientInfo, setShowPatientInfo] = useState(true);

  // 원본 데이터 저장 (변경사항 체크용)
  const [originalData] = useState({
    patientId: groundTruth?.patientId?.toString() || "",
    promptType: groundTruth?.promptType || "",
    expectedContent: groundTruth?.expectedContent || "",
  });

  // 환자 정보 조회
  const { data: patientResponse, isLoading: isLoadingPatient } = useQuery({
    queryKey: ["patients", formData.patientId],
    queryFn: () => fetchPatientById(Number(formData.patientId)),
    enabled: !!formData.patientId && formData.patientId.toString().length > 0,
  });

  const patient = patientResponse?.data;

  // 변경사항이 있는지 체크
  const hasChanges = () => {
    if (!isEditMode) return true; // 생성 모드에서는 항상 true

    return (
      formData.patientId !== originalData.patientId ||
      formData.promptType !== originalData.promptType ||
      formData.expectedContent !== originalData.expectedContent
    );
  };

  // 버튼 비활성화 조건
  const isSubmitDisabled = () => {
    // 제출 중일 때
    if (isSubmitting) return true;

    // 환자 ID가 없을 때
    if (!formData.patientId) return true;

    // 프롬프트 타입이 없을 때
    if (!formData.promptType) return true;

    // 예상 결과가 없을 때
    if (!formData.expectedContent.trim()) return true;

    // 환자 정보 로딩 중일 때
    if (isLoadingPatient) return true;

    // 환자 정보가 없을 때 (입력했는데 해당 환자가 없는 경우)
    if (formData.patientId && !isLoadingPatient && !patient) return true;

    // 수정 모드에서 변경사항이 없을 때
    if (isEditMode && !hasChanges()) return true;

    return false;
  };

  const promptTypeOptions = [
    { value: "", label: "선택하세요" },
    {
      value: PromptType.MEDICATION_COUNSELING,
      label: PROMPT_TYPE_KOREAN_NAMES[PromptType.MEDICATION_COUNSELING],
    },
    { value: PromptType.CMM_2, label: PROMPT_TYPE_KOREAN_NAMES[PromptType.CMM_2] },
    { value: PromptType.LCMM, label: PROMPT_TYPE_KOREAN_NAMES[PromptType.LCMM] },
    { value: PromptType.FREE_QUESTION, label: PROMPT_TYPE_KOREAN_NAMES[PromptType.FREE_QUESTION] },
  ];

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.patientId) {
      newErrors.patientId = "환자 ID를 입력하세요.";
    }

    if (!formData.promptType) {
      newErrors.promptType = "프롬프트 타입을 선택하세요.";
    }

    if (!formData.expectedContent.trim()) {
      newErrors.expectedContent = "예상 결과를 입력하세요.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    onSubmit({
      patientId: Number(formData.patientId),
      promptType: formData.promptType,
      expectedContent: formData.expectedContent,
    });
  };

  return (
    <FormContainer>
      <FormGroup>
        <Label>환자 ID</Label>
        <InputWrapper>
          <Input
            type="number"
            placeholder="환자 ID를 입력하세요"
            value={formData.patientId}
            onChange={(e) => setFormData({ ...formData, patientId: e.target.value })}
            error={!!errors.patientId}
            hint={errors.patientId}
            style={{ minHeight: "44px" }}
          />
          <ToggleButton
            type="Icon"
            icon={showPatientInfo ? "Chevron Up" : "Chevron Down"}
            size="Medium"
            variant="Outlined"
            color="Blue"
            active={showPatientInfo}
            onClick={() => setShowPatientInfo(!showPatientInfo)}
          />
        </InputWrapper>
      </FormGroup>

      {showPatientInfo && (patient || isLoadingPatient) && (
        <PatientInfoBox>
          {isLoadingPatient ? (
            <Spinner size="Small" />
          ) : patient ? (
            <>
              <PatientInfoRow>
                <InfoLabel>생년월일:</InfoLabel>
                <InfoValue>{patient.birthDate}</InfoValue>
              </PatientInfoRow>
              <PatientInfoRow>
                <InfoLabel>성별:</InfoLabel>
                <InfoValue>
                  {patient.gender === "M"
                    ? "남성"
                    : patient.gender === "F"
                    ? "여성"
                    : patient.gender}
                </InfoValue>
              </PatientInfoRow>
              {patient.diseases && (
                <PatientInfoRow>
                  <InfoLabel>질병명:</InfoLabel>
                  <InfoValue>{patient.diseases}</InfoValue>
                </PatientInfoRow>
              )}
              {patient.allergies && (
                <PatientInfoRow>
                  <InfoLabel>알레르기:</InfoLabel>
                  <InfoValue>{patient.allergies}</InfoValue>
                </PatientInfoRow>
              )}
            </>
          ) : (
            <NoPatientMessage>환자 정보를 찾을 수 없습니다.</NoPatientMessage>
          )}
        </PatientInfoBox>
      )}

      <FormGroup>
        <Label>프롬프트 타입</Label>
        <Dropdown
          defaultValue="프롬프트 타입 선택"
          selected={
            promptTypeOptions.find((opt) => opt.value === formData.promptType)?.label ||
            "선택하세요"
          }
          size="Medium"
          active={showPromptDropdown}
          onClick={() => setShowPromptDropdown(!showPromptDropdown)}
        >
          {promptTypeOptions.map((option) => (
            <DropdownMenuItem
              key={option.value}
              onClick={() => {
                setFormData({ ...formData, promptType: option.value });
                setShowPromptDropdown(false);
              }}
              isSelected={formData.promptType === option.value}
            >
              {option.label}
            </DropdownMenuItem>
          ))}
        </Dropdown>
      </FormGroup>

      <FormGroup>
        <Label>예상 결과</Label>
        <Input
          multiLine
          placeholder="Ground Truth 내용을 입력하세요"
          value={formData.expectedContent}
          onChange={(e) => setFormData({ ...formData, expectedContent: e.target.value })}
          error={!!errors.expectedContent}
          hint={errors.expectedContent}
          style={{ height: "400px" }}
        />
        {!errors.expectedContent && <HelperText>Markdown 형식을 지원합니다.</HelperText>}
      </FormGroup>

      <ButtonGroup>
        <Button
          size="Medium"
          type="Text"
          variant="Outlined"
          color="Secondary"
          onClick={onCancel}
          disabled={isSubmitting}
          style={{ flex: 1 }}
        >
          취소
        </Button>
        <Button
          size="Medium"
          type="Text"
          variant="Contained"
          color="Primary"
          disabled={isSubmitDisabled()}
          disabledColor={isSubmitDisabled()}
          onClick={handleSubmit}
          style={{ flex: 1 }}
        >
          {isSubmitting ? <Spinner size="Small" /> : isEditMode ? "수정" : "생성"}
        </Button>
      </ButtonGroup>
    </FormContainer>
  );
};

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
`;

const InputWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;

  & > div:first-child {
    flex: 1;
  }
`;

const PatientInfoBox = styled.div`
  padding: 16px;
  background-color: ${COLOR_SYSTEM.get("Blue")?.[50]};
  border: 1px solid ${COLOR_SYSTEM.get("Blue")?.[200]};
  border-radius: 8px;
`;

const PatientInfoRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.span`
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
`;

const InfoValue = styled.span`
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
`;

const NoPatientMessage = styled.p`
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  text-align: center;
`;

const HelperText = styled.span`
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
`;

const ButtonGroup = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

export default GroundTruthForm;
